import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_URL, JITSI_DOMAIN, getTokenSubject } from '../demoMode';
import { ScreenShell } from './ScreenShared';

function sanitizeRoomSegment(value) {
  return String(value || 'guest').toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 32) || 'guest';
}

function normalizeJitsiDomain(domain) {
  const cleaned = String(domain || '').trim();
  if (!cleaned) {
    return 'localhost:8443';
  }

  return cleaned.replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

// Must match the id embedded in the iframe URL hash; Jitsi only emits
// IFrame API events when this id is present and the postis handshake completes.
const JITSI_EXTERNAL_API_ID = 0;
const POSTIS_SCOPE = `jitsi_meet_external_api_${JITSI_EXTERNAL_API_ID}`;

function JitsiCallView() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [resolvedDomain, setResolvedDomain] = useState(normalizeJitsiDomain(JITSI_DOMAIN));
  const iframeRef = useRef(null);
  const hasLeftRef = useRef(false);
  const participantCountRef = useRef(0);
  const sessionStartedRef = useRef(false);
  // Lets startBillingSession end the call without a circular useCallback dependency.
  const handleLeaveRef = useRef(() => {});

  const target = sanitizeRoomSegment(id);
  const roomName = useMemo(() => `ic-${target}`, [target]);
  const baseUrl = `https://${resolvedDomain}/${roomName}`;
  const jitsiUrl = `${baseUrl}#jitsi_meet_external_api_id=${JITSI_EXTERNAL_API_ID}&config.prejoinPageEnabled=false&config.startWithAudioMuted=false&config.startWithVideoMuted=false&config.enableClosePage=false&config.disableDeepLinking=true`;

  const startBillingSession = useCallback(() => {
    if (sessionStartedRef.current) {
      return;
    }
    sessionStartedRef.current = true;

    const token = localStorage.getItem('token');
    // Room ic-{target} belongs to the callee; only the visiting caller pays.
    if (!token || !target || getTokenSubject(token) === target) {
      return;
    }

    fetch(`${API_URL}/v1/sessions/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ target })
    })
      .then((response) => response.json().catch(() => null))
      .then((payload) => {
        if (payload && payload.allowed === false) {
          sessionStartedRef.current = false;
          handleLeaveRef.current();
        }
      })
      .catch(() => {
        // Retry on the next participant-joined event.
        sessionStartedRef.current = false;
      });
  }, [target]);

  const handleLeave = useCallback(() => {
    if (hasLeftRef.current) {
      return;
    }
    hasLeftRef.current = true;

    try {
      const token = localStorage.getItem('token');
      if (token && target) {
        fetch(`${API_URL}/v1/sessions/end`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ target, reason: 'ended' })
        }).catch(() => {});
      }
    } catch (_err) {
      // Ignore background session end failures
    }

    // Bring user back to the chat screen with their conversation partner
    const nextPath = target ? `/chats/${target}` : '/chats';
    navigate(nextPath);
  }, [navigate, target]);

  useEffect(() => {
    handleLeaveRef.current = handleLeave;
  }, [handleLeave]);

  useEffect(() => {
    let active = true;

    const loadDomain = async () => {
      try {
        const response = await fetch(`${API_URL}/v1/jitsi-domain`);
        if (!response.ok) {
          return;
        }
        const payload = await response.json().catch(() => null);
        const domain = normalizeJitsiDomain(payload?.domain || JITSI_DOMAIN);
        if (active && domain) {
          setResolvedDomain(domain);
        }
      } catch (_err) {
        // Keep the configured fallback domain.
      }
    };

    loadDomain();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      let data = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (_err) {
          return;
        }
      }

      if (!data || typeof data !== 'object') {
        return;
      }

      // Jitsi wraps IFrame API traffic in the postis protocol. It buffers all
      // events until the parent echoes its __ready__ ping, so answer it here.
      if (data.postis === true && data.scope === POSTIS_SCOPE) {
        if (data.method === '__ready__') {
          iframeRef.current?.contentWindow?.postMessage(
            JSON.stringify({ postis: true, scope: POSTIS_SCOPE, method: '__ready__', params: data.params }),
            '*'
          );
          return;
        }

        if (data.method === 'message') {
          const eventName = data.params?.data?.name || '';

          if (eventName === 'video-conference-joined') {
            participantCountRef.current = 1;
          } else if (eventName === 'participant-joined') {
            participantCountRef.current += 1;
            // Billing starts only once both people are actually in the room.
            if (participantCountRef.current >= 2) {
              startBillingSession();
            }
          } else if (eventName === 'participant-left') {
            participantCountRef.current = Math.max(participantCountRef.current - 1, 0);
            // The other person left: stop billing and return to chat.
            if (sessionStartedRef.current) {
              handleLeave();
            }
          } else if (eventName === 'video-conference-left' || eventName === 'video-ready-to-close') {
            handleLeave();
          }
        }
        return;
      }

      // Compat fallback for JitsiMeetExternalAPI-style payloads.
      const eventName = data.name || data.type || data.event || '';
      if (
        eventName === 'videoConferenceLeft' ||
        eventName === 'video-conference-left' ||
        eventName === 'readyToClose' ||
        eventName === 'video-ready-to-close'
      ) {
        handleLeave();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleLeave, startBillingSession]);

  return (
    <ScreenShell
      titleBar={
        <div className="figma-call-topbar">
          <button
            type="button"
            className="figma-toplink"
            onClick={handleLeave}
            aria-label="Back to chat"
          >
            ‹ Back to chat
          </button>
          <span className="figma-toptext">Live Call • {target}</span>
        </div>
      }
      contentClassName="ic-call-screen-content"
    >
      <div className="ic-call-iframe-container">
        <iframe
          ref={iframeRef}
          src={jitsiUrl}
          title={`Jitsi call with ${target}`}
          allow="camera; microphone; fullscreen; display-capture; autoplay"
          className="ic-call-iframe"
        />
      </div>

      {/* 
        Open full screen is disabled for now per user requirements, but preserved in case we want to bring it back.
        <div className="figma-grid-two">
          <a href={baseUrl} target="_blank" rel="noreferrer" className="figma-btn figma-btn-primary">
            Open full screen
          </a>
        </div>
      */}
    </ScreenShell>
  );
}

export default JitsiCallView;